import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import UserServices from './Services/Consumers/UserServices'
import { NamedRoutes } from './routes'

const CurrencyContext = createContext({})

const CurrencyProvider = ({ children, currencyId, isEdit, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()

  const [currencyData, setCurrencyData] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [currencyDataFailure, setCurrencyDataFailure] = React.useState(false)
  const [deleteCurrencyLoading, setDeleteCurrencyLoading] = React.useState(false)

  React.useEffect(() => {
    if (isEdit && !!currencyId) {
      getCurrencyData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCurrencyData = () => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      setCurrencyDataFailure(false)
      UserServices.getCurrency(currencyId, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }).then((result) => {
        if (!result || result.status !== 200 || !result.data?.data) {
          setCurrencyDataFailure(true)
        } else {
          setCurrencyData(result.data.data)
        }
      }).catch(() => {
        setCurrencyDataFailure(true)
      }).finally(() => {
        setIsLoading(false)
      })
    }
  }

  const handleSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      UserServices.submitCurrency(
        currencyId,
        isEdit,
        values,
        { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
        null,
        null,
        FormikActions
      ).then((result) => {
        if (result && result.status === 200) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: isEdit ? 'Currencies.SuccessUpdate' : 'Currencies.SuccessCreate' }),
            color: 'success'
          }, 'success')
          history.push(NamedRoutes.workspace.currencies.list)
        } else {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result?.response?.data?.status?.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message,
            color: 'error'
          }, 'error')
        }
      }).catch(() => {
        handleNotificationCall({
          type: 'defence',
          message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
          color: 'error'
        }, 'error')
      })
    }
  }

  const handleDeleteCurrency = (index, data, setData, setCount, handleDeleteCurrencyDialog) => {
    if (!data || !data[index]) {
      return
    }
    if (!!keycloak?.authenticated) {
      setDeleteCurrencyLoading(true)
      UserServices.deleteCurrency(
        data[index].id,
        { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
        setDeleteCurrencyLoading
      ).then((result) => {
        if ((!!result && result.response && result.response.status !== 200) || !result || result.status !== 200) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result?.response?.data?.status?.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message,
            color: 'error'
          }, 'error')
        } else {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: 'Currencies.SuccessDelete' }),
            color: 'success'
          }, 'success')
          setData((prevData) => {
            const newData = [...prevData]
            newData.splice(index, 1)
            return newData
          })
          setCount((prevCount) => prevCount - 1)
        }
      }).catch(() => {
        handleNotificationCall({
          type: 'defence',
          message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
          color: 'error'
        }, 'error')
      }).finally(() => {
        setDeleteCurrencyLoading(false)
        handleDeleteCurrencyDialog()
      })
    }
  }

  return (
    <CurrencyContext.Provider value={{
      currencyData,
      isLoading,
      currencyDataFailure,
      isEdit,
      getCurrencyData,
      handleSubmit,
      deleteCurrencyLoading,
      handleDeleteCurrency
    }}>
      {children}
    </CurrencyContext.Provider>
  )
}

const CurrencyConsumer = CurrencyContext.Consumer

export { CurrencyProvider, CurrencyConsumer, CurrencyContext }
